import { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Typography,
  Badge,
  Row,
  Col,
  message,
  Statistic,
  Alert,
  Tabs,
  Table,
  Tag,
  Progress,
  Space,
  Select,
  DatePicker,
  Input,
  Modal,
  Timeline,
  Avatar,
  Tooltip,
  Divider,
  Switch,
  List,
  Empty,
  Spin
} from "antd";
import {
  ArrowRightOutlined,
  LeftOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  GlobalOutlined,
  BookOutlined,
  SettingOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  HistoryOutlined,
  UserOutlined,
  CalendarOutlined,
  FireOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  BarChartOutlined,
  BellOutlined,
  StarOutlined,
  TrophyOutlined,
  CrownOutlined,
  RocketOutlined,
  TeamOutlined,
  AuditOutlined,
  BulbOutlined,
  TwitterOutlined
} from "@ant-design/icons";
import apiClient from "../../services/apiClient";
import { getSampleCountData } from "./sampleNewsData";
import { ThemeContext } from "../../context/ThemeContext";
import "./AdminApproval.css";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Search } = Input;

const AdminApprovalComponent = () => {
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedSource, setSelectedSource] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [autoApprove, setAutoApprove] = useState(false);

  const [counts, setCounts] = useState({
    lmCount: 0,
    bsCount: 0,
    feCount: 0,
    etCount: 0,
    mcCount: 0,
    twitterCount: 0
  });

  const [approvalStats, setApprovalStats] = useState({
    totalPending: 0,
    approvedToday: 0,
    rejectedToday: 0,
    avgApprovalTime: 0,
    approvalRate: 0,
    urgentItems: 0
  });

  const [recentApprovals, setRecentApprovals] = useState([]);
  const [pendingArticles, setPendingArticles] = useState([]);
  const [error, setError] = useState(null);

  const newsSources = useMemo(() => [
    {
      key: "livemint",
      title: "Livemint News",
      description: "Financial and business news from Livemint",
      icon: <FileTextOutlined />,
      color: "#1890ff",
      route: "/admin/approval/news",
      count: counts.lmCount,
      priority: "high",
      lastUpdated: "2 hours ago"
    },
    {
      key: "businessstandard",
      title: "Business Standard",
      description: "Comprehensive business and market coverage",
      icon: <DollarOutlined />,
      color: "#52c41a",
      route: "/admin/approval/news",
      count: counts.bsCount,
      priority: "medium",
      lastUpdated: "1 hour ago"
    },
    {
      key: "financialexpress",
      title: "Financial Express",
      description: "Financial markets and economic analysis",
      icon: <BarChartOutlined />,
      color: "#722ed1",
      route: "/admin/approval/news",
      count: counts.feCount,
      priority: "high",
      lastUpdated: "30 minutes ago"
    },
    {
      key: "economictimes",
      title: "Economic Times",
      description: "Leading economic and business news",
      icon: <GlobalOutlined />,
      color: "#fa8c16",
      route: "/admin/approval/news",
      count: counts.etCount,
      priority: "critical",
      lastUpdated: "15 minutes ago"
    },
    {
      key: "moneycontrol",
      title: "Money Control",
      description: "Investment and market intelligence",
      icon: <BookOutlined />,
      color: "#eb2f96",
      route: "/admin/approval/news",
      count: counts.mcCount,
      priority: "medium",
      lastUpdated: "45 minutes ago"
    },
    {
      key: "twitter",
      title: "Twitter News",
      description: "Latest trending finance tweets",
      icon: <TwitterOutlined />,
      color: "#1DA1F2",
      route: "/admin/approval/news",
      count: counts.twitterCount,
      priority: "medium",
      lastUpdated: "10 minutes ago"
    }
  ], [counts]);

  // Mock data for pending articles
  const mockPendingArticles = [
    {
      id: 1,
      title: "RBI Announces New Monetary Policy Framework",
      source: "Economic Times",
      author: "Financial Reporter",
      submittedAt: "2024-01-15 10:30:00",
      priority: "high",
      category: "Monetary Policy",
      wordCount: 850,
      status: "pending",
      tags: ["RBI", "Monetary Policy", "Banking"]
    },
    {
      id: 2,
      title: "Stock Market Reaches New All-Time High",
      source: "Business Standard",
      author: "Market Analyst",
      submittedAt: "2024-01-15 09:15:00",
      priority: "medium",
      category: "Markets",
      wordCount: 650,
      status: "pending",
      tags: ["Stock Market", "NSE", "BSE"]
    },
    {
      id: 3,
      title: "Government Announces New Tax Reforms",
      source: "Financial Express",
      author: "Policy Expert",
      submittedAt: "2024-01-15 08:45:00",
      priority: "critical",
      category: "Taxation",
      wordCount: 1200,
      status: "pending",
      tags: ["Tax Reforms", "Government", "Policy"]
    },
    {
      id: 4,
      title: "Tech Startups Raise Record Funding",
      source: "Livemint",
      author: "Tech Reporter",
      submittedAt: "2024-01-15 07:30:00",
      priority: "medium",
      category: "Technology",
      wordCount: 750,
      status: "pending",
      tags: ["Startups", "Funding", "Technology"]
    },
    {
      id: 5,
      title: "Global Economic Outlook 2024",
      source: "Money Control",
      author: "Economic Analyst",
      submittedAt: "2024-01-15 06:20:00",
      priority: "high",
      category: "Global Economy",
      wordCount: 950,
      status: "pending",
      tags: ["Global Economy", "2024", "Outlook"]
    }
  ];

  // Mock data for recent approvals
  const mockRecentApprovals = [
    {
      id: 1,
      title: "Banking Sector Performance Analysis",
      source: "Economic Times",
      approvedBy: "admin@example.com",
      approvedAt: "2024-01-15 11:00:00",
      action: "approved",
      reviewTime: "15 minutes"
    },
    {
      id: 2,
      title: "Cryptocurrency Market Trends",
      source: "Business Standard",
      approvedBy: "admin@example.com",
      approvedAt: "2024-01-15 10:45:00",
      action: "rejected",
      reviewTime: "8 minutes",
      reason: "Inaccurate market data"
    },
    {
      id: 3,
      title: "Real Estate Market Update",
      source: "Financial Express",
      approvedBy: "admin@example.com",
      approvedAt: "2024-01-15 10:30:00",
      action: "approved",
      reviewTime: "12 minutes"
    }
  ];

  useEffect(() => {
    console.log("Counts updated:", counts);
  }, [counts]);
  useEffect(() => {
    initializeData();
    fetchLatestNewCount();
  }, []);

  const initializeData = async () => {
    setLoading(true);
    try {
      // Use sample data since backend is down
      const sampleCounts = getSampleCountData();
      setCounts(sampleCounts);

      // Set mock approval stats
      setApprovalStats({
        totalPending: 15,
        approvedToday: 8,
        rejectedToday: 2,
        avgApprovalTime: 12.5,
        approvalRate: 85.7,
        urgentItems: 3
      });

      // Set mock data
      setPendingArticles(mockPendingArticles);
      setRecentApprovals(mockRecentApprovals);

      message.info("Using sample data for testing. Backend server is down.");
    } catch (error) {
      console.error("Error initializing data:", error);
      setError("Failed to load approval data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestNewCount = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/news/count`);
      console.log("Fetched news counts:", response.data);

      if (response && response.data && response.data.data) {
        const response1 = response.data.data;
        setCounts({
          bsCount: response1.businessStandard || 0,
          feCount: response1.financialExpress || 0,
          etCount: response1.economicTimes || 0,
          mcCount: response1.mcCount || 0,
          lmCount: response1.liveMint || 0,
          twitterCount: response1.twitter || 0
        });
        message.success("Counts refreshed successfully!");
      }
    } catch (error) {
      console.error("Error fetching news count:", error);
      message.warning("Backend server is down. Using sample data.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewsApproval = (source) => {
    console.log(source);
    navigate(source.route, {
      state: { data: source.key },
    });
  };

  const handleArticleApproval = (article, action) => {
    setSelectedArticle(article);
    setApprovalModalVisible(true);
  };

  const confirmApproval = async (action) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update local state
      if (action === 'approve') {
        setPendingArticles(prev => prev.filter(item => item.id !== selectedArticle.id));
        setRecentApprovals(prev => [{
          id: Date.now(),
          title: selectedArticle.title,
          source: selectedArticle.source,
          approvedBy: "admin@example.com",
          approvedAt: new Date().toISOString(),
          action: "approved",
          reviewTime: "5 minutes"
        }, ...prev]);
        message.success(`Article "${selectedArticle.title}" approved successfully!`);
      } else {
        setPendingArticles(prev => prev.filter(item => item.id !== selectedArticle.id));
        setRecentApprovals(prev => [{
          id: Date.now(),
          title: selectedArticle.title,
          source: selectedArticle.source,
          approvedBy: "admin@example.com",
          approvedAt: new Date().toISOString(),
          action: "rejected",
          reviewTime: "3 minutes",
          reason: "Content guidelines violation"
        }, ...prev]);
        message.success(`Article "${selectedArticle.title}" rejected successfully!`);
      }

      setApprovalModalVisible(false);
      setSelectedArticle(null);
    } catch (error) {
      message.error("Failed to process approval. Please try again.");
    }
  };

  const getTotalPendingCount = () => {
    return Object.values(counts).reduce((total, count) => total + count, 0);
  };

  const getStatusColor = (count) => {
    if (count === 0) return "#52c41a"; // Green for no pending
    if (count < 10) return "#faad14"; // Yellow for low count
    return "#ff4d4f"; // Red for high count
  };

  const getStatusText = (count) => {
    if (count === 0) return "No Pending";
    if (count === 1) return "1 Pending";
    return `${count} Pending`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "critical": return "#ff4d4f";
      case "high": return "#fa8c16";
      case "medium": return "#1890ff";
      case "low": return "#52c41a";
      default: return "#d9d9d9";
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "critical": return <FireOutlined />;
      case "high": return <ExclamationCircleOutlined />;
      case "medium": return <ClockCircleOutlined />;
      case "low": return <CheckCircleOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const getActionColor = (action) => {
    return action === "approved" ? "#52c41a" : "#ff4d4f";
  };

  const getActionIcon = (action) => {
    return action === "approved" ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />;
  };

  // Table columns for pending articles
  const pendingColumns = [
    {
      title: "Article",
      dataIndex: "title",
      key: "title",
      width:'50%',
      render: (text, record) => (
        <div className="article-info">
          <div className="article-title">{text}</div>
          <div className="article-meta">
            <Tag color="blue">{record.source}</Tag>
            <Tag color="green">{record.category}</Tag>
            <Text type="secondary">{record.wordCount} words</Text>
          </div>
        </div>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (priority) => (
        <Tag color={getPriorityColor(priority)} icon={getPriorityIcon(priority)}>
          {priority.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Submitted",
      dataIndex: "submittedAt",
      key: "submittedAt",
      render: (text) => (
        <div className="submission-info">
          <Text>{new Date(text).toLocaleDateString()}</Text>
          <Text type="secondary">{new Date(text).toLocaleTimeString()}</Text>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleArticleApproval(record, 'view')}
            style={{ 
              backgroundColor: '#1890ff', 
              borderColor: '#1890ff',
              borderRadius: 8,
              color: 'white',
              fontWeight: 500
            }}
          >
            View
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleArticleApproval(record, 'approve')}
            style={{ 
              backgroundColor: '#52c41a', 
              borderColor: '#52c41a',
              borderRadius: 8,
              color: 'white',
              fontWeight: 500
            }}
          >
            Approve
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => handleArticleApproval(record, 'reject')}
            style={{ 
              backgroundColor: '#ff4d4f', 
              borderColor: '#ff4d4f',
              borderRadius: 8,
              color: 'white',
              fontWeight: 500
            }}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  // Quick stats data
  const quickStats = [
    {
      title: "Total Pending",
      value: getTotalPendingCount(),
      prefix: <ExclamationCircleOutlined />,
      color: "#ff4d4f",
      suffix: "articles",
      trend: "up"
    },
    {
      title: "Approved Today",
      value: approvalStats.approvedToday,
      prefix: <CheckCircleOutlined />,
      color: "#52c41a",
      suffix: "articles",
      trend: "up"
    },
    {
      title: "Avg. Review Time",
      value: `${approvalStats.avgApprovalTime}m`,
      prefix: <ClockCircleOutlined />,
      color: "#1890ff",
      suffix: "per article",
      trend: "stable"
    },
    {
      title: "Approval Rate",
      value: `${approvalStats.approvalRate}%`,
      prefix: <TrophyOutlined />,
      color: "#722ed1",
      suffix: "success rate",
      trend: "up"
    }
  ];

  if (loading) {
    return (
      <div className="approval-loading">
        <Spin size="large" />
        <Text>Loading approval dashboard...</Text>
      </div>
    );
  }

  return (
    <div className={`approval-dashboard-container ${isDark ? 'dark' : ''}`}>
      {/* Hero Section */}
      <div
        className="admin-approval-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 18,
          padding: '2px',
          marginBottom: 32,
          position: 'relative',
        }}
      >
        <div
          style={{
            background: 'transparent',
            borderRadius: 16,
            padding: "48px 48px 40px 48px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            position: 'relative',
            overflow: 'hidden',
            minHeight: 180,
            width: '100%',
            height: '100%',
          }}
        >
          {/* Overlay for better text readability */}
          {isDark && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)',
                borderRadius: 16,
              }}
            />
          )}
          
          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Title
                level={1}
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontWeight: 800,
                  fontSize: 42,
                  color: isDark ? "#ffffff" : "#1a1a1a",
                  textShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
                }}
              >
                Content Approval Dashboard
              </Title>
              <Text 
                style={{ 
                  fontSize: 20, 
                  color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
                  fontWeight: 400,
                  lineHeight: 1.4,
                  textShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none',
                }}
              >
                Manage and approve content from various news sources with advanced workflow controls
              </Text>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Button
                type="default"
                icon={<SettingOutlined />}
                onClick={() => navigate("/admin")}
                style={{
                  borderRadius: 8,
                  height: 40,
                  fontWeight: 500,
                  backgroundColor: isDark ? '#3a3a4e' : undefined,
                  borderColor: isDark ? '#4a4a5e' : undefined,
                  color: isDark ? '#ffffff' : undefined
                }}
              >
                Admin Dashboard
              </Button>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={fetchLatestNewCount}
                loading={loading}
                style={{
                  borderRadius: 8,
                  height: 40,
                  fontWeight: 500,
                  backgroundColor: isDark ? '#8b5cf6' : undefined,
                  borderColor: isDark ? '#8b5cf6' : undefined
                }}
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="approval-content">
        {/* Stats Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
          {quickStats.map((stat, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <div
                style={{
                  background: isDark ? "#2a2a3e" : "#fff",
                  border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
                  borderRadius: 12,
                  boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
                  padding: "20px",
                  position: "relative",
                  height: 200,
                  overflow: "hidden"
                }}
              >
                <div style={{ 
                  position: "absolute",
                  top: "16px",
                  left: "16px",
                  fontSize: 19,
                  fontWeight: 600,
                  fontStyle: "Semi Bold",
                  color: isDark ? "#ffffff" : "#000",
                  zIndex: 2
                }}>
                  {stat.title}
                </div>
                <div style={{ 
                  position: "absolute",
                  bottom: "20px",
                  left: "16px",
                  fontSize: 36, 
                  fontWeight: 700, 
                  fontStyle: "bold",
                  color: isDark ? "#ffffff" : "#000",
                  lineHeight: 1,
                  zIndex: 1
                }}>
                  {stat.value}
                </div>
                <div style={{ 
                  position: "absolute",
                  bottom: "16px",
                  right: "16px",
                  fontSize: 12, 
                  color: stat.color,
                  fontWeight: 500
                }}>
                  {stat.suffix}
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Main Content Tabs */}
        <Card className="main-content-card">
          {/* Custom Tabs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, backgroundColor: isDark ? '#2a2a3e' : '#f5f5f5', borderRadius: 12, padding: 4, marginBottom: 24 }}>
            <div
              onClick={() => setActiveTab("overview")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "overview" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 200
              }}
            >
              <BarChartOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Overview</span>
            </div>
            
            <div
              onClick={() => setActiveTab("pending")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "pending" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 200
              }}
            >
              <ClockCircleOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Pending Articles</span>
            </div>
            
            <div
              onClick={() => setActiveTab("analytics")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "analytics" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 200
              }}
            >
              <BarChartOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Analytics</span>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="overview-content">
              <Row gutter={[24, 24]}>
                {/* News Sources */}
                <Col xs={24} lg={18}>
                  <div style={{ 
                    backgroundColor: isDark ? '#2a2a3e' : '#f5f5f5', 
                    borderRadius: 12, 
                    padding: '24px',
                    height: '100%'
                  }}>
                    <div style={{ marginBottom: 24 }}>
                      <Title level={4} style={{ 
                        margin: 0, 
                        marginBottom: 8, 
                        fontSize: 24, 
                        fontWeight: 600,
                        color: isDark ? '#ffffff' : '#000'
                      }}>
                        News Sources
                      </Title>
                      <Text style={{ 
                        fontSize: 16, 
                        color: isDark ? 'rgba(255, 255, 255, 0.8)' : '#666',
                        lineHeight: 1.4
                      }}>
                        Click on any source to review and approve pending articles
                      </Text>
                    </div>
                    
                    <Row gutter={[16, 16]}>
                      {newsSources.map((source) => (
                        <Col xs={24} sm={12} lg={8} key={source.key}>
                          <Card
                            hoverable
                            onClick={() => handleNewsApproval(source)}
                            style={{
                              borderRadius: '12px !important',
                              boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                              border: isDark ? '1px solid #3a3a4e' : '1px solid #d9d9d9',
                              height: '100%',
                              overflow: 'hidden',
                              backgroundColor: isDark ? '#3a3a4e' : '#ffffff'
                            }}
                            bodyStyle={{
                              padding: '20px',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              borderRadius: '12px !important',
                              overflow: 'hidden',
                              backgroundColor: isDark ? '#3a3a4e' : '#ffffff'
                            }}
                          >
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'flex-start',
                              marginBottom: 12
                            }}>
                              <Title level={5} style={{ 
                                margin: 0, 
                                fontSize: 18, 
                                fontWeight: 600,
                                color: isDark ? '#ffffff' : '#000'
                              }}>
                                {source.title}
                              </Title>
                              <Tag 
                                color={getPriorityColor(source.priority)}
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  borderRadius: 6,
                                  margin: 0
                                }}
                              >
                                {source.priority.toUpperCase()}
                              </Tag>
                            </div>
                            
                            <Text style={{ 
                              fontSize: 14, 
                              color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#666',
                              lineHeight: 1.4,
                              marginBottom: 16,
                              flex: 1
                            }}>
                              {source.description}
                            </Text>
                            
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center'
                            }}>
                              <Text style={{ 
                                fontSize: 12, 
                                color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#999'
                              }}>
                                {source.lastUpdated}
                              </Text>
                              <Button
                                type="primary"
                                style={{ 
                                  backgroundColor: isDark ? '#8b5cf6' : '#1890ff',
                                  borderColor: isDark ? '#8b5cf6' : '#1890ff',
                                  borderRadius: 8,
                                  fontSize: 14,
                                  fontWeight: 500,
                                  height: 36,
                                  paddingLeft: 16,
                                  paddingRight: 16
                                }}
                              >
                                Review Articles
                              </Button>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Col>

                {/* Recent Activity */}
                <Col xs={24} lg={6}>
                  <Card 
                    style={{
                      borderRadius: 12,
                      boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      border: isDark ? '1px solid #3a3a4e' : 'none',
                      height: '100%',
                      backgroundColor: isDark ? '#2a2a3e' : '#ffffff'
                    }}
                    bodyStyle={{
                      padding: '24px',
                      height: '100%',
                      backgroundColor: isDark ? '#2a2a3e' : '#ffffff'
                    }}
                  >
                    <div style={{ marginBottom: 24 }}>
                      <Title level={4} style={{ 
                        margin: 0, 
                        fontSize: 20, 
                        fontWeight: 600,
                        color: isDark ? '#ffffff' : '#000',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <HistoryOutlined style={{ color: isDark ? '#ffffff' : undefined }} /> Recent Activity
                      </Title>
                    </div>
                    <Timeline className="activity-timeline">
                      {recentApprovals.map((approval) => (
                        <Timeline.Item
                          key={approval.id}
                          color={getActionColor(approval.action)}
                          dot={getActionIcon(approval.action)}
                        >
                          <div className="timeline-content">
                            <div className="timeline-title">{approval.title}</div>
                            <div className="timeline-details">
                              <Text type="secondary">{approval.source}</Text>
                              <Tag color={getActionColor(approval.action)}>
                                {approval.action.toUpperCase()}
                              </Tag>
                            </div>
                            <div className="timeline-meta">
                              <Text type="secondary">{approval.reviewTime} ago</Text>
                            </div>
                          </div>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </Card>
                </Col>
              </Row>
            </div>
          )}

          {activeTab === 'pending' && (
            <div className="pending-content">
                    <div className="filters-section">
                      <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} md={8}>
                          <Search
                            placeholder="Search articles..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                          />
                        </Col>
                        <Col xs={24} md={6}>
                          <Select
                            placeholder="Filter by source"
                            value={selectedSource}
                            onChange={setSelectedSource}
                            style={{ width: '100%' }}
                          >
                            <Option value="all">All Sources</Option>
                            {newsSources.map(source => (
                              <Option key={source.key} value={source.key}>{source.title}</Option>
                            ))}
                          </Select>
                        </Col>
                        <Col xs={24} md={6}>
                          <RangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            style={{ width: '100%' }}
                          />
                        </Col>
                        <Col xs={24} md={4}>
                          <Button
                            type="primary"
                            icon={<FilterOutlined />}
                            block
                          >
                            Filter
                          </Button>
                        </Col>
                      </Row>
                    </div>

                    <Table
                      columns={pendingColumns}
                      dataSource={pendingArticles}
                      rowKey="id"
                      className="custom-table"
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} articles`
                      }}
                    />
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="analytics-content">
              <div style={{ 
                backgroundColor: '#fff', 
                borderRadius: 12, 
                padding: '24px',
                border: '1px solid #d9d9d9',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}>
                <Row gutter={[32, 24]}>
                  <Col xs={24} lg={12}>
                    <div>
                      <Title level={4} style={{ 
                        margin: 0, 
                        marginBottom: 24, 
                        fontSize: 20, 
                        fontWeight: 600,
                        color: '#000'
                      }}>
                        Approval Performance
                      </Title>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>Approval Rate</Text>
                          <Text style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>85.5%</Text>
                        </div>
                        <Progress
                          percent={85.5}
                          strokeColor="#52c41a"
                          trailColor="#f0f0f0"
                          showInfo={false}
                          style={{ marginBottom: 8 }}
                        />
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>Average Review Time</Text>
                          <Text style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>75%</Text>
                        </div>
                        <Progress
                          percent={75}
                          strokeColor="#1890ff"
                          trailColor="#f0f0f0"
                          showInfo={false}
                          style={{ marginBottom: 8 }}
                        />
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>Urgent Items</Text>
                          <Text style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>25%</Text>
                        </div>
                        <Progress
                          percent={25}
                          strokeColor="#ff4d4f"
                          trailColor="#f0f0f0"
                          showInfo={false}
                        />
                      </div>
                    </div>
                  </Col>
                  
                  <Col xs={24} lg={12}>
                    <div>
                      <Title level={4} style={{ 
                        margin: 0, 
                        marginBottom: 24, 
                        fontSize: 20, 
                        fontWeight: 600,
                        color: '#000'
                      }}>
                        Source Performance
                      </Title>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {[
                          { name: 'Live Mint News', percent: 85.5, color: '#1890ff' },
                          { name: 'Business Standard', percent: 75, color: '#722ed1' },
                          { name: 'Financial Express', percent: 70, color: '#fa8c16' },
                          { name: 'Economic Times', percent: 75, color: '#13c2c2' },
                          { name: 'Money Control', percent: 50, color: '#eb2f96' },
                          { name: 'Twitter News', percent: 90, color: '#52c41a' }
                        ].map((source, index) => (
                          <div key={index}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                              <Text style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>{source.name}</Text>
                              <Text style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>{source.percent}%</Text>
                            </div>
                            <Progress
                              percent={source.percent}
                              strokeColor={source.color}
                              trailColor="#f0f0f0"
                              showInfo={false}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Approval Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            Review Article
          </Space>
        }
        open={approvalModalVisible}
        onCancel={() => setApprovalModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setApprovalModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="reject"
            danger
            icon={<CloseOutlined />}
            onClick={() => confirmApproval('reject')}
          >
            Reject
          </Button>,
          <Button
            key="approve"
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => confirmApproval('approve')}
          >
            Approve
          </Button>
        ]}
        width={800}
        className="approval-modal"
      >
        {selectedArticle && (
          <div className="article-review">
            <div className="article-header">
              <Title level={4}>{selectedArticle.title}</Title>
              <div className="article-meta">
                <Tag color="blue">{selectedArticle.source}</Tag>
                <Tag color="green">{selectedArticle.category}</Tag>
                <Tag color={getPriorityColor(selectedArticle.priority)}>
                  {selectedArticle.priority.toUpperCase()}
                </Tag>
              </div>
            </div>
            <Divider />
            <div className="article-content">
              <Paragraph>
                This is a sample article content. In a real implementation,
                this would contain the actual article text that needs to be reviewed.
                The content would be fetched from the backend API and displayed here
                for the admin to review before making an approval decision.
              </Paragraph>
              <Paragraph>
                Additional paragraphs would be displayed here with proper formatting,
                including any images, links, or other media content that needs to be
                reviewed as part of the approval process.
              </Paragraph>
            </div>
            <Divider />
            <div className="article-footer">
              <div className="article-stats">
                <Text type="secondary">Author: {selectedArticle.author}</Text>
                <Text type="secondary">Word Count: {selectedArticle.wordCount}</Text>
                <Text type="secondary">Submitted: {new Date(selectedArticle.submittedAt).toLocaleString()}</Text>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminApprovalComponent;
